import axios from 'axios';
import { resolveCrmApiRequestUrl } from './api-origin';
import { usesDevProxy } from './backend';
import { isAuthFailureLogoutDisabled, isLoginAuthenticateRequest, logSuppressedAuthFailure, shouldForceLogoutOn401, } from './auth-failure-policy';
import { refreshAccessToken } from '@/shared/auth/auth.reducer';
import { getAccessToken, getRefreshToken } from '@/shared/auth/jwt-storage';
import { clearStoredAuth } from '@/shared/auth/auth-storage';
import { clearAuthState } from '@/shared/auth/auth.reducer';
import { store } from '@/store/store';
const REQUEST_TIMEOUT_MS = 5 * 60 * 1000;
let refreshInFlight = null;
function shouldAttachBearer(url) {
    /** Refresh-call must omit bearer — same URL check as {@link ../../../Monolithic_App_Frontend}. */
    return Boolean(url && !url.includes('user-management/api/app/refresh-token'));
}
function shouldAttemptRefreshOn401(url) {
    if (!url)
        return false;
    if (url.includes('user-management/api/app/refresh-token'))
        return false;
    if (isLoginAuthenticateRequest(url))
        return false;
    return Boolean(getRefreshToken());
}
async function refreshAccessTokenOnce() {
    if (!getRefreshToken())
        return false;
    if (!refreshInFlight) {
        refreshInFlight = store
            .dispatch(refreshAccessToken())
            .unwrap()
            .then(() => Boolean(getAccessToken()))
            .catch(() => false)
            .finally(() => {
            refreshInFlight = null;
        });
    }
    return refreshInFlight;
}
function notifyNetworkIfOffline(err) {
    const code = err.code;
    if (code === 'ERR_NETWORK' || code === 'ECONNABORTED' || err.message === 'Network Error') {
        console.warn('Network issue while calling API:', err.config?.url, err.message);
    }
}
function shouldSkipRedirectAfterAuthFailure(cfgUrl) {
    return isLoginAuthenticateRequest(cfgUrl);
}
/**
 * Bearer + SSO handling aligned with {@link ../../../Monolithic_App_Frontend/src/app/config/axios-interceptor.ts}.
 * Call once at bootstrap — before routers or thunks rely on axios.
 */
export function setupAxiosAuthInterceptors() {
    axios.defaults.timeout = REQUEST_TIMEOUT_MS;
    axios.interceptors.request.use((config) => {
        config.headers = config.headers ?? {};
        const url = resolveCrmApiRequestUrl(config.url ?? '');
        config.url = url;
        /** Relative IAM paths use same-origin only when webpack dev proxy is on. */
        if (usesDevProxy() &&
            (url.startsWith('/user/') ||
                url.startsWith('/user-management/') ||
                url.startsWith('/audit-log/'))) {
            config.baseURL = '';
        }
        const token = shouldAttachBearer(url) ? getAccessToken() : null;
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        try {
            const locale = typeof window !== 'undefined' ? window.sessionStorage.getItem('locale') : null;
            const fallback = locale || 'en';
            config.headers['Accept-Language'] = fallback === 'ar-ly' ? 'ar-LY' : fallback;
        }
        catch {
            /* ignore */
        }
        return config;
    });
    axios.interceptors.response.use((res) => res, async (err) => {
        const cfgUrl = err.config?.url ?? '';
        const status = err.response?.status ?? 0;
        const originalConfig = err.config;
        if (status === 401 && originalConfig && shouldAttemptRefreshOn401(cfgUrl)) {
            if (!originalConfig._retriedAfterRefresh) {
                const refreshed = await refreshAccessTokenOnce();
                if (refreshed) {
                    originalConfig._retriedAfterRefresh = true;
                    originalConfig.headers = originalConfig.headers ?? {};
                    const token = getAccessToken();
                    if (token) {
                        originalConfig.headers.Authorization = `Bearer ${token}`;
                    }
                    return axios(originalConfig);
                }
            }
        }
        if (status === 401 || status === 403) {
            if (status === 403) {
                if (isAuthFailureLogoutDisabled()) {
                    logSuppressedAuthFailure(err, '403 Forbidden — permission denied for this API; session is unchanged.');
                }
                notifyNetworkIfOffline(err);
                return Promise.reject(err);
            }
            if (!shouldForceLogoutOn401(cfgUrl)) {
                logSuppressedAuthFailure(err, isLoginAuthenticateRequest(cfgUrl)
                    ? 'Login failed — staying on the sign-in form.'
                    : '401 Unauthorized — auto-logout disabled for local debugging.');
                if (isLoginAuthenticateRequest(cfgUrl)) {
                    clearStoredAuth();
                    store.dispatch(clearAuthState());
                }
                notifyNetworkIfOffline(err);
                return Promise.reject(err);
            }
            clearStoredAuth();
            store.dispatch(clearAuthState());
            try {
                if (typeof window !== 'undefined') {
                    const onLoginLike = window.location.pathname === '/login' || window.location.pathname === '/auth/login';
                    const skipNavigate = onLoginLike || shouldSkipRedirectAfterAuthFailure(cfgUrl);
                    if (!skipNavigate) {
                        window.location.assign('/login');
                    }
                }
            }
            catch {
                /* ignore */
            }
        }
        notifyNetworkIfOffline(err);
        return Promise.reject(err);
    });
}
